import { canUser, ops } from '@weenat/client/dist/core/access'
import useVirtualDevice from '@weenat/client/dist/core/devices/useVirtualDevice'
import { useOrgPlotCounts } from '@weenat/client/dist/core/plots'
import { BillingStatus, PlanName } from '@weenat/client/dist/enums'
import { useIntl } from '@weenat/wintl'
import { useOrgContext } from 'app/orgProvider'
import { useNavigate } from 'app/routx-router'
import useDisclosure from 'app/src/hooks/useDisclosure'
import Button from 'app/src/kit/Button'
import Icons from 'app/src/kit/Icons'
import Modal from 'app/src/kit/Modal'
import Text from 'app/src/kit/Text'
import PanelSectionTitle from 'app/src/kit/panels/PanelSectionTitle'
import AskForMoreVWS from 'app/src/vws/AskForMoreVWS'
import logEvent from 'app/utils/analytics'
import { isNil } from 'lodash-es'
import { memo } from 'react'
import AddModalItem from './AddModalItem'

const AvailableWithSubscriptionText = ({ plan }: { plan: PlanName | null }) => {
  const { t } = useIntl()

  return plan === 'essential' ? (
    <Text $fontSize='sm'>{t('ecommerce.plans.restrictions.only_premium_users')}</Text>
  ) : null
}

/**
 * Use absolute paths inside this component since it is rendered at many paths
 */
const AddModal = () => {
  const { t } = useIntl()
  const nav = useNavigate()
  const org = useOrgContext()

  const { isOpen: isAddModalOpen, close, open } = useDisclosure()
  const { isOpen: isAskForMoreOpen, open: openAskForMore, close: closeAskForMore } = useDisclosure()

  const closeAddModal = () => {
    close()
  }

  const openAddModal = () => {
    logEvent('add_md')
    open()
  }

  // Getting the plan & remaining virtual devices
  // Essential users cannot create more plots than they have virtual devices
  const plan = BillingStatus[org.billingStatus] as PlanName

  const { remainingVirtualDeviceActivations, quota } = useVirtualDevice({ orgId: org.id })

  const { remaining: remainingPlotCreation } = useOrgPlotCounts(org)

  const canEditNetwork = canUser(ops.edit, 'networks', { organization: org })
  const canEditOptions = canUser(ops.edit, 'options', { organization: org })
  const canAddMember = canUser(ops.edit, 'members', { organization: org })
  const canAddPlot = canUser(ops.create, 'plots', { organization: org })
  const canAddDevice = canUser(ops.linkDevice, 'organizations', { organization: org })
  const hasEditionRoleInOrg = canAddMember || canAddPlot || canEditOptions || canAddDevice

  const administrationPath = `/farms/${org.id}/admin` as const

  return (
    <>
      <Icons.PlusSignCircle
        $size='xl'
        $p={'sm'}
        $color={'grayscale.white'}
        $backgroundColor={'primary.500'}
        onPress={openAddModal}
      />
      <Modal
        title={t('addModelModal.title')}
        isOpen={isAddModalOpen}
        close={() => {
          logEvent('add_md_close')
          closeAddModal()
        }}
        width={480}
        height={540}
        minHeight={hasEditionRoleInOrg ? 320 : undefined}
        contentContainerProps={{ style: { padding: 8 } }}
      >
        {/* Org related actions */}
        {hasEditionRoleInOrg && (
          <Flex $p={'md'} $flexDirection='column' $gap='md'>
            <PanelSectionTitle>{org.name}</PanelSectionTitle>

            {canAddDevice && (
              <AddModalItem
                Icon={Icons.SingleSensorFilled}
                onPress={() => {
                  close()
                  nav(`${administrationPath}/devices/add`)
                }}
                event='add_md_device'
                label={t('addModelModal.device', { capitalize: true })}
              />
            )}

            {canAddDevice && (
              <AddModalItem
                Icon={Icons.SingleVirtualSensorFilled}
                onPress={() => {
                  close()
                  nav(`/farms/${org.id}/add-virtual-weather-station`)
                }}
                event='add_md_vws'
                isDisabled={
                  !isNil(remainingVirtualDeviceActivations) &&
                  remainingVirtualDeviceActivations <= 0
                }
                label={t('models.device.common_names.virtual_weather_station', {
                  capitalize: true
                })}
                buttonSlot={
                  isNil(remainingVirtualDeviceActivations) ? (
                    <Text $fontSize='sm'>∞</Text>
                  ) : remainingVirtualDeviceActivations <= 0 ? (
                    <Button
                      variant='compact'
                      onPress={(e) => {
                        e.stopPropagation()
                        logEvent('add_md_more_vws')
                        openAskForMore()
                      }}
                    >
                      {t('actions.get_more_stations')}
                    </Button>
                  ) : (
                    <Text $fontSize='sm'>
                      {t('models.plot.actions.virtual_device_remaining_activations_with_total', {
                        total: quota ?? '?',
                        remaining: remainingVirtualDeviceActivations
                      })}
                    </Text>
                  )
                }
              />
            )}

            {canAddPlot && (
              <AddModalItem
                Icon={Icons.PlotsFilled}
                onPress={() => {
                  close()
                  nav(`/farms/${org.id}/plots/create`)
                }}
                event='add_md_plot'
                isDisabled={
                  !isNil(remainingPlotCreation) &&
                  remainingPlotCreation <= 0 &&
                  org.billingStatus === BillingStatus.essential
                }
                label={t('addModelModal.plot', { capitalize: true })}
                description={
                  plan === 'essential' ? (
                    <Text $fontSize='sm'>
                      {t('models.plot.actions.virtual_device_remaining_activations', {
                        value: remainingPlotCreation as number
                      })}
                    </Text>
                  ) : null
                }
              />
            )}

            {canEditOptions && (
              <AddModalItem
                Icon={Icons.AddOption}
                onPress={() => {
                  close()
                  nav(`${administrationPath}/options`)
                }}
                event='add_md_integration'
                label={t('addModelModal.integration', { capitalize: true })}
              />
            )}

            {canAddMember && (
              <AddModalItem
                Icon={Icons.Person}
                onPress={() => {
                  close()
                  nav(`${administrationPath}/members/invite`)
                }}
                event='add_md_member'
                label={t('addModelModal.member', { capitalize: true })}
                description={<AvailableWithSubscriptionText plan={plan} />}
              />
            )}
          </Flex>
        )}

        {/*  Network related actions */}
        {canEditNetwork ? (
          <Flex $p={'md'} $flexDirection='column' $gap='md'>
            <PanelSectionTitle>
              {t('models.network.model.singular_name', { capitalize: true })}
            </PanelSectionTitle>

            <AddModalItem
              Icon={Icons.GroupPersons}
              onPress={() => {
                close()
                nav(`${administrationPath}/network/invite`)
              }}
              event='add_md_affiliate'
              label={t('addModelModal.networkOrganization', { capitalize: true })}
            />
          </Flex>
        ) : null}

        {/* Account related actions */}
        <Flex $p={'md'} $flexDirection='column' $gap='md'>
          <PanelSectionTitle>{t('titles.my_account', { capitalize: true })}</PanelSectionTitle>
          <AddModalItem
            Icon={Icons.Campaign}
            onPress={() => {
              close()
              nav('/alerts/create', { search: { origin: `/farms/${org.id}` } })
            }}
            event='add_md_alert'
            label={t('addModelModal.alert', { capitalize: true })}
            description={<AvailableWithSubscriptionText plan={plan} />}
          />
        </Flex>
      </Modal>
      <AskForMoreVWS isOpen={isAskForMoreOpen} close={closeAskForMore} />
    </>
  )
}

export default memo(AddModal)
